import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Folded or open, remembered per panel in this browser.
 *
 * For the context panels on the ticket's sidebar: they start folded to one
 * line, so the ticket's own settings stay on the first screen, and a person
 * who always wants one open gets it open. Storage is a convenience -- a
 * private window simply starts folded.
 */
export function usePanelFold(key: string, openByDefault = false): [boolean, () => void] {
  const storageKey = `tributary.fold.${key}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? openByDefault : v === 'open';
    } catch {
      return openByDefault;
    }
  });
  const toggle = () =>
    setOpen((o) => {
      try {
        localStorage.setItem(storageKey, o ? 'closed' : 'open');
      } catch {
        /* a convenience only */
      }
      return !o;
    });
  return [open, toggle];
}

/** The panel title as the fold's toggle, with its one-line summary beside it. */
export function FoldTitle({
  open,
  onToggle,
  title,
  summary,
}: {
  open: boolean;
  onToggle: () => void;
  title: string;
  summary: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-1.5 rounded px-1 text-left hover:bg-surface-hover"
    >
      {open ? (
        <ChevronDown aria-hidden className="h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
      ) : (
        <ChevronRight aria-hidden className="h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
      )}
      <span className="shrink-0">{title}</span>
      <span className="min-w-0 truncate text-2xs font-normal">{summary}</span>
    </button>
  );
}
